import { FC, KeyboardEvent, ReactNode, useState } from "react";
import { Box, Fade, InputAdornment, Stack, TextField, Typography, css, styled } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { IconVisibility, IconVisibilityOff } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, blue, divider, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";

export type TPasswordGateProps = {
  /** Expected password for client-side comparison */
  password: string;
  children: ReactNode;
  title?: string;
  description?: string;
  /** Optional async validator — overrides client-side password check when provided */
  onValidate?: (pw: string) => boolean | Promise<boolean>;
};

export const PasswordGate: FC<TPasswordGateProps> = ({
  password,
  children,
  title = "パスワードの入力",
  description = "情報を確認するにはパスワードを入力してください。",
  onValidate,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      setError("パスワードを入力してください。");
      return;
    }
    setIsLoading(true);
    setError("");

    const isValid = onValidate ? await onValidate(inputValue) : inputValue === password;

    if (isValid) {
      setTimeout(() => {
        setIsUnlocked(true);
        setIsLoading(false);
      }, 500);
    } else {
      setError("パスワードが正しくありません。");
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
  };

  const handleBack = () => {
    setIsUnlocked(false);
    setInputValue("");
    setError("");
  };

  // Unlocked state: fade in children with back button
  if (isUnlocked) {
    return (
      <Fade in timeout={400}>
        <Box>
          {children}
          <Stack alignItems="center" mt={3} mb={3}>
            <Button variant="ghost" onClick={handleBack}>
              パスワード入力に戻る
            </Button>
          </Stack>
        </Box>
      </Fade>
    );
  }

  // Locked state: password form
  return (
    <GateWrapper>
      <GateCard>
        <Stack direction="column" textAlign="center">
          <Typography variant="head20Bold" textAlign="center">
            {title}
          </Typography>
          <Typography variant="body14" color={text.secondary} textAlign="center" mt={2}>
            {description}
          </Typography>
        </Stack>

        <Stack mt={4} gap={1}>
          <Typography variant="body14Semi">
            パスワード <span style={{ color: state.error_1 }}>*</span>
          </Typography>
          <StyledTextField
            type={showPassword ? "text" : "password"}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="••••••••"
            error={!!error}
            disabled={isLoading}
            InputProps={{
              endAdornment: inputValue ? (
                <InputAdornment position="end">
                  <IconBox onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <IconVisibilityOff fontSize="20px" /> : <IconVisibility fontSize="20px" />}
                  </IconBox>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {error && (
            <Typography variant="cap12" color={state.error_1}>
              {error}
            </Typography>
          )}
        </Stack>

        <Stack mt={3}>
          <Button variant="primary" fullWidth onClick={handleSubmit} isLoading={isLoading}>
            送信
          </Button>
        </Stack>
      </GateCard>
    </GateWrapper>
  );
};

const GateWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  padding-top: 48px;
`;

const GateCard = styled(Box)`
  background: ${background.white};
  border-radius: ${rounded.sm};
  padding: 40px 32px;
  width: 100%;
  max-width: 480px;
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    background: ${background.white};
    padding-right: 8px;
    input {
      color: ${text.primary};
      caret-color: ${blue[70]};
      padding: 10px 12px;
      ${css(typography.body14)};
      &::placeholder {
        opacity: 1;
        color: ${text.tertiary};
      }
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1} !important;
      }
    }
  }
`;
